import { use, useMemo } from 'react';

import path from 'path';

import { Stack } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, useColDefs } from '~components';
import { pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType } from '~enums';
import DeviceContext from '~features/devices/providers/deviceContext';
import OrganisationContext from '~features/organisations/providers/organisationDetailsContext';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import ScheduledAction, { scheduledActionGridCols } from '../../interfaces/scheduledAction';
import ScheduledActionsService from '../../services/scheduledActionsService';

const scheduledActionsService = getService(ScheduledActionsService);

interface ScheduledActionsDataGridProps {
	source: 'device' | 'organisation';
	deviceId?: string;
	overwrittenClientId?: string;
}

const ScheduledActionsDataGrid = ({
	source,
	deviceId,
	overwrittenClientId,
}: ScheduledActionsDataGridProps) => {
	const { t } = useTranslation();
	const isDevice = source === 'device';
	const persistenceKey = 'scheduledActions';

	const apiRef = useGridApiRef();
	const [searchParams] = useSearchParams();
	const deviceContext = use(DeviceContext);
	const organisationContext = use(OrganisationContext);

	const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;

	const { data, error, isLoading, isValidating } = useSWR(
		scheduledActionsService.getScheduledActionsForDeviceCacheKey(
			source,
			deviceId,
			overwrittenClientId,
			resourceGroupId
		),
		async (_args: (string | undefined)[], options: { signal?: AbortSignal }) => {
			if (deviceId != null) {
				return scheduledActionsService.getScheduledActionsForDevice(
					{
						deviceId: deviceId,
						deviceOrganisationId: overwrittenClientId,
						resourceGroupId: resourceGroupId,
					},
					options.signal
				);
			}
			if (overwrittenClientId != null) {
				const scheduledActions = await scheduledActionsService.getScheduledActionsForOrganisation(
					overwrittenClientId,
					resourceGroupId ?? undefined,
					options.signal
				);
				return resourceGroupId == null ? scheduledActions : (
						scheduledActions.filter(
							(scheduledAction) => scheduledAction.resourceGroupId === resourceGroupId
						)
					);
			}
			throw new Error('Device id or organisation id is required to load scheduled actions.');
		},
		{
			keepPreviousData: false,
			use: [cancellationMiddleware],
		}
	);

	const scheduledActionsLinkProperties = {
		linkPath: path.join(
			isDevice ? pagePathSegments.Devices : pagePathSegments.Organisations,
			isDevice ? (deviceId ?? '') : (overwrittenClientId ?? ''),
			pagePathSegments.ScheduledActions,
			':id'
		),
		pathQueryParametersGetter: () => {
			return [
				[queryParameters.OverwrittenClientId, overwrittenClientId?.toString()],
			] as ParamKeyValuePair[];
		},
		linkState: {
			source: isDevice ? 'device' : 'organisation',
			device: isDevice && {
				id: deviceId,
				name: deviceContext.device?.name,
			},
			organisation: !isDevice && {
				id: overwrittenClientId,
				name: organisationContext.organisation?.name,
			},
		},
	};
	const colDefs = useColDefs<ScheduledAction>();
	const columns = useMemo((): GridColDef<ScheduledAction>[] => {
		const cols = [
			colDefs.id(
				scheduledActionsLinkProperties.linkPath,
				scheduledActionsLinkProperties.pathQueryParametersGetter,
				true,
				scheduledActionsLinkProperties.linkState
			),
			colDefs.name(
				true,
				scheduledActionsLinkProperties.linkPath,
				scheduledActionsLinkProperties.pathQueryParametersGetter,
				undefined,
				scheduledActionsLinkProperties.linkState
			),
			colDefs.gridColDefFor(scheduledActionGridCols.cronExpression),
			colDefs.startDate((value: ScheduledAction) => {
				const date = value.start ?? value.dateTime;
				return date ? dayjs(date) : undefined;
			}),
			colDefs.endDate((value: ScheduledAction) => (value.end ? dayjs(value.end) : undefined)),
			colDefs.resourceGroup((value: ScheduledAction) => value.resourceGroupId?.toString() ?? ''),
		];
		return cols;
	}, [colDefs, isDevice, deviceId, overwrittenClientId]);

	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.ResourceGroup,
			organisationId: overwrittenClientId,
			isInternal: false,
		},
	];

	return (
		<Stack direction='column' sx={{ width: 1 }} gap={Dimens.spacingMd}>
			<DataGridToolbar filters={actionBarFilters} />
			<DataGrid
				apiRef={apiRef}
				persistenceKey={persistenceKey}
				getRowHeight={() => 'auto'}
				disableRowSelectionOnClick
				error={error != null}
				loading={isLoading || isValidating}
				columns={columns}
				noDataTitle={t('device.status.noScheduledActionsYetTitle', { ns: 'entities' })}
				noDataDescription={t('device.status.noScheduledActionsYetDescription', { ns: 'entities' })}
				rows={data ?? []}
				paginationMode='client'
				sx={{
					flexGrow: 1,
					width: 1,
					height: '100%',
					overflowX: 'auto',
					'& .MuiDataGrid-cell': {
						alignItems: 'center',
						py: 1,
					},
				}}
			/>
		</Stack>
	);
};

export default ScheduledActionsDataGrid;
